import { useQuery, useMutation, useQueryClient, } from '@tanstack/react-query';
import { admin } from '../api/methods';
export const adminKeys = {
    members: ['admin', 'members'],
    employees: ['admin', 'employees'],
    managers: ['admin', 'managers'],
    pending: ['admin', 'pending'],
    memberAccess: ['admin', 'member-access'],
    settings: ['admin', 'settings'],
    billingMode: ['admin', 'billing-mode'],
    customerTaxRules: ['admin', 'customer-tax-rules'],
    auditLog: (opts) => ['admin', 'audit-log', opts.limit ?? null, opts.offset ?? null],
    adminActionsLog: (opts) => ['admin', 'admin-actions-log', opts.limit ?? null, opts.offset ?? null, opts.action ?? null],
    impersonationLog: (opts) => ['admin', 'impersonation-log', opts.limit ?? null, opts.offset ?? null],
};
/**
 * Optimistically patch a single member in the cached members list and return
 * the prior snapshot so `onError` can roll the row back. We cancel in-flight
 * member fetches first so a slower refetch can't clobber the optimistic value.
 */
async function patchMemberOptimistic(queryClient, memberId, patch) {
    await queryClient.cancelQueries({ queryKey: adminKeys.members });
    const prev = queryClient.getQueryData(adminKeys.members);
    if (prev) {
        queryClient.setQueryData(adminKeys.members, prev.map((m) => (m.id === memberId ? { ...m, ...patch } : m)));
    }
    return prev;
}
export function useAdminMembers(options) {
    return useQuery({
        queryKey: adminKeys.members,
        queryFn: () => admin.getMembers(),
        enabled: options?.enabled !== false,
    });
}
export function useAdminManagers(options) {
    return useQuery({
        queryKey: adminKeys.managers,
        queryFn: () => admin.getManagers(),
        enabled: options?.enabled !== false,
    });
}
export function useUpdateMember() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ memberId, data }) => admin.updateMember(memberId, data),
        onMutate: async ({ memberId, data }) => {
            // Optimistically reflect simple scalar edits so the select doesn't snap
            // back to its old value while the refetch is in flight. Role is not
            // editable here (#2103: managed in Ayrical).
            const patch = {};
            if ('region' in data)
                patch.region = data.region ?? undefined;
            if ('employee_id' in data)
                patch.employeeId = data.employee_id ?? undefined;
            if ('default_review_shop_key' in data) {
                patch.defaultReviewShopKey = data.default_review_shop_key ?? null;
            }
            const prev = await patchMemberOptimistic(queryClient, memberId, patch);
            return { prev };
        },
        onError: (_err, _vars, ctx) => {
            if (ctx?.prev)
                queryClient.setQueryData(adminKeys.members, ctx.prev);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.members });
        },
    });
}
export function useAssignManager() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ memberId, managerMemberId }) => admin.assignManager(memberId, managerMemberId),
        onMutate: async ({ memberId, managerMemberId }) => {
            // Hold the Assigned-Manager select on its new value until refetch.
            // managerName is reconciled by the members refetch in onSettled.
            const prev = await patchMemberOptimistic(queryClient, memberId, {
                managerMemberId: managerMemberId ?? undefined,
            });
            return { prev };
        },
        onError: (_err, _vars, ctx) => {
            if (ctx?.prev)
                queryClient.setQueryData(adminKeys.members, ctx.prev);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.members });
        },
    });
}
export function usePendingMembers(options) {
    return useQuery({
        queryKey: adminKeys.pending,
        queryFn: () => admin.getPendingMembers(),
        enabled: options?.enabled !== false,
    });
}
// ---- #2103 member management through Ayrical ----
// The server applies Ayrical's grant rules; the client only offers what
// `useMemberAccess` and each member's `canManage` allow. Every mutation
// refreshes members, pending invitations and the managers picker (a role
// change can add or remove a manager).
export function useMemberAccess(options) {
    return useQuery({
        queryKey: adminKeys.memberAccess,
        queryFn: () => admin.getMemberAccess(),
        enabled: options?.enabled !== false,
        retry: false,
    });
}
function invalidateMemberQueries(queryClient) {
    queryClient.invalidateQueries({ queryKey: adminKeys.members });
    queryClient.invalidateQueries({ queryKey: adminKeys.pending });
    queryClient.invalidateQueries({ queryKey: adminKeys.managers });
}
export function useInviteMember() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => admin.inviteMember(data),
        onSettled: () => invalidateMemberQueries(queryClient),
    });
}
export function useChangeMemberAccessRole() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ memberId, accessRole }) => admin.changeMemberAccessRole(memberId, accessRole),
        onSettled: () => invalidateMemberQueries(queryClient),
    });
}
export function useSuspendMember() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ memberId, reason }) => admin.suspendMember(memberId, reason),
        onSettled: () => invalidateMemberQueries(queryClient),
    });
}
export function useReactivateMember() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ memberId, reason }) => admin.reactivateMember(memberId, reason),
        onSettled: () => invalidateMemberQueries(queryClient),
    });
}
export function useResendInvitation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (membershipId) => admin.resendInvitation(membershipId),
        onSettled: () => invalidateMemberQueries(queryClient),
    });
}
export function useRevokeInvitation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (membershipId) => admin.revokeInvitation(membershipId),
        onSettled: () => invalidateMemberQueries(queryClient),
    });
}
// ---- #668 Admin > Employees (labor-picker roster) ----
// Mutations also invalidate the catalog employees queries so the ticket
// labor picker reflects adds/deactivations without a reload.
export function useAdminEmployees(options) {
    return useQuery({
        queryKey: adminKeys.employees,
        queryFn: () => admin.getEmployees(),
        enabled: options?.enabled !== false,
    });
}
function invalidateEmployeeQueries(queryClient) {
    queryClient.invalidateQueries({ queryKey: adminKeys.employees });
    queryClient.invalidateQueries({ queryKey: ['catalog', 'employees'] });
}
export function useCreateAdminEmployee() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => admin.createEmployee(data),
        onSuccess: () => invalidateEmployeeQueries(queryClient),
    });
}
export function useUpdateAdminEmployee() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ employeeId, data }) => admin.updateEmployee(employeeId, data),
        onSuccess: () => invalidateEmployeeQueries(queryClient),
    });
}
export function useAdminSettings() {
    return useQuery({
        queryKey: adminKeys.settings,
        queryFn: () => admin.getSettings(),
    });
}
export function useSetAdminSetting() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ key, enabled }) => admin.setSetting(key, enabled),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.settings });
            // The billing-mode toggle (Review Queue) reads the same flag, so keep it
            // in sync when an admin flips it from the Settings tab.
            queryClient.invalidateQueries({ queryKey: adminKeys.billingMode });
        },
    });
}
export function useCustomerTaxRules(options = {}) {
    return useQuery({
        queryKey: adminKeys.customerTaxRules,
        queryFn: () => admin.getCustomerTaxRules(),
        enabled: options.enabled ?? true,
    });
}
function useInvalidateCustomerTaxRules() {
    const queryClient = useQueryClient();
    return () => {
        queryClient.invalidateQueries({ queryKey: adminKeys.customerTaxRules });
        queryClient.invalidateQueries({ queryKey: ['customer', 'summary'] });
    };
}
export function useCreateCustomerTaxRule() {
    const invalidate = useInvalidateCustomerTaxRules();
    return useMutation({
        mutationFn: (input) => admin.createCustomerTaxRule(input),
        onSuccess: invalidate,
    });
}
export function useUpdateCustomerTaxRule() {
    const invalidate = useInvalidateCustomerTaxRules();
    return useMutation({
        mutationFn: ({ ruleId, input }) => admin.updateCustomerTaxRule(ruleId, input),
        onSuccess: invalidate,
    });
}
export function useDeleteCustomerTaxRule() {
    const invalidate = useInvalidateCustomerTaxRules();
    return useMutation({
        mutationFn: (ruleId) => admin.deleteCustomerTaxRule(ruleId),
        onSuccess: invalidate,
    });
}
/**
 * Manager-reachable read of the org-wide estimate/invoice switch. Backed by the
 * same `qbo_invoice_mode` flag as useAdminSettings, but its own endpoint is
 * gated on `manage_billing_mode` so Managers (who can't reach Admin) can read it
 * from the Review Queue.
 */
export function useBillingMode() {
    return useQuery({
        queryKey: adminKeys.billingMode,
        queryFn: () => admin.getBillingMode(),
    });
}
export function useSetBillingMode() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (enabled) => admin.setBillingMode(enabled),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.billingMode });
            // Admin > Settings tab renders the same flag; refresh it too so the two
            // surfaces never drift after a Review Queue flip.
            queryClient.invalidateQueries({ queryKey: adminKeys.settings });
        },
    });
}
// ---- #455 Admin > Audit tab reads ----
// Each source has its own hook + enabled flag so the tab's segmented control
// can enable only the active source's query (switching sources must not fire
// all three at once).
export function useAdminAuditLog(opts = {}) {
    return useQuery({
        queryKey: adminKeys.auditLog(opts),
        queryFn: () => admin.getAuditLog(opts),
        enabled: opts?.enabled !== false,
    });
}
export function useAdminActionsLog(opts = {}) {
    return useQuery({
        queryKey: adminKeys.adminActionsLog(opts),
        queryFn: () => admin.getAdminActionsLog(opts),
        enabled: opts?.enabled !== false,
    });
}
export function useImpersonationLog(opts = {}) {
    return useQuery({
        queryKey: adminKeys.impersonationLog(opts),
        queryFn: () => admin.getImpersonationLog(opts),
        enabled: opts?.enabled !== false,
    });
}
//# sourceMappingURL=useAdmin.js.map